import React, { useCallback, useMemo, useState } from 'react';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { TSubmitFunc } from '~/src/components/RequestFormPhone';
import { CDN_API_ENDPOINT } from '~/src/constants';
import { NON_BREAKING_SPACE } from '~/src/constants/simbols';
import { useIntersectionObserver } from '~/src/helpers/hooks/useIntersectionObserver';

import { imagesWithDomofon, imagesWithoutDomofon } from './data';

import * as ST from './styled';

const IMG_PATH = `${CDN_API_ENDPOINT}/static/images/domofon/connect-domofon`;

enum EMediaWidth {
    mobile = '(max-width: 767px)',
}

export interface IConnectDomofon {
    hasDomofon: boolean,
    submitRequest: TSubmitFunc,
    onStartFormFilling?: () => void,
}

export const ConnectDomofon = ({ hasDomofon, submitRequest, onStartFormFilling }: IConnectDomofon): JSX.Element => {
    const isMobile = useMatchMedia(EMediaWidth.mobile);
    const [isVisible, setIsVisible] = useState(false);

    const backgroundImg = useWebp()
        ? require('~/static/images/domofon/connect-domofon/connect-bg.webp')
        : require('~/static/images/domofon/connect-domofon/connect-bg.png');

    const observeHandler = useCallback((entries) => {
        const [firstElem] = entries || [];
        setIsVisible((isVisible) => isVisible || firstElem?.isIntersecting);
    }, []);

    const blockRef = useIntersectionObserver(observeHandler, { root: null, threshold: 0.4 });

    const animatedImages = useMemo(() => {
        return hasDomofon ? imagesWithDomofon : imagesWithoutDomofon;
    }, [hasDomofon]);

    const appsBtnClickHandler = useCallback((app: string) => {
        dataLayerPush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationButton,
            label: app,
            action: '',
        });
    }, []);

    return (
        <ST.ConnectDomofon hasDomofon={hasDomofon} ref={blockRef} backgroundImg={backgroundImg || ''}>
            <ST.SWideContainer>
                {hasDomofon
                    ? <ST.Description>
                        <ST.TitleMin>
                            Откройте все возможности вашего нового домофона
                        </ST.TitleMin>

                        <ST.Text hasDomofon={hasDomofon}>
                            Всего за два простых шага
                        </ST.Text>

                        <ST.Steps>
                            <ST.FirstStepWrap>
                                <ST.StepInner>
                                    <ST.StepDescription>
                                        <ST.StepTitle>Шаг 1.</ST.StepTitle>
                                        <ST.StepText>{'Установите\nприложение\n«Умный Дом.ру»'}</ST.StepText>
                                    </ST.StepDescription>
                                    <ST.QRCodeWrap>
                                        <img
                                            src={require('~/static/images/domofon/connect-domofon/qr-intelect-dom.svg')}
                                            alt={'QR-код для установки приложения'}
                                            data-test='qr-code-img'
                                        />
                                    </ST.QRCodeWrap>
                                </ST.StepInner>
                                <ST.StepBtns>
                                    <ST.SMobileStoreButton
                                        onClick={() => appsBtnClickHandler('click btn android')}
                                        store='GooglePlay'
                                        data-test='google-play'
                                        width='109px'
                                        height='30px'
                                        btnType={'OUTLINE'}
                                        link={'https://play.google.com/store/apps/details?id=com.ertelecom.smarthome'}
                                    />
                                    <ST.SMobileStoreButton
                                        onClick={() => appsBtnClickHandler('click btn apple')}
                                        store='AppStore'
                                        data-test='app-store'
                                        width='103px'
                                        height='30px'
                                        btnType={'OUTLINE'}
                                        link={'https://apps.apple.com/ru/app/%D1%83%D0%BC%D0%BD%D1%8B%D0%B9-%D0%B4%D0%BE%D0%BC-%D1%80%D1%83/id1373157818'}
                                    />
                                </ST.StepBtns>
                            </ST.FirstStepWrap>
                            <ST.StepWrap>
                                <ST.StepDescription>
                                    <ST.StepTitle>Шаг 2.</ST.StepTitle>
                                    <ST.StepText>{`Зарегистрируйтесь и${NON_BREAKING_SPACE}подключите услугу «Домофон»`}</ST.StepText>
                                </ST.StepDescription>
                                <ST.StepImgWrap>
                                    <img
                                        src={require('~/static/images/domofon/connect-domofon/step-img-2.png')}
                                        width='152'
                                        height='106'
                                        alt='Зарегистрируйтесь и подключите услугу «Домофон»'
                                    />
                                </ST.StepImgWrap>
                            </ST.StepWrap>
                        </ST.Steps>

                        <ST.SubTitle>
                            <i>Всё готово!</i>Самое время попробовать новые функции в деле
                        </ST.SubTitle>
                    </ST.Description>
                    /* Нет домофона */
                    : <ST.Description>
                        <ST.Title>
                            {`Подключите умный домофон и${NON_BREAKING_SPACE}держите всё под контролем`}
                        </ST.Title>
                        <ST.Text>
                            {`Придомовая территория 24/7 находится под наблюдением видеокамер.
                            Вы всегда сможете посмотреть, что происходит у${NON_BREAKING_SPACE}подъезда прямо с${NON_BREAKING_SPACE}телефона.`}
                        </ST.Text>
                        <ST.SRequestFormPhone
                            templateVariant={'second'}
                            isLoading={false}
                            isMobile={isMobile}
                            submitRequest={submitRequest}
                            onStartFormFilling={onStartFormFilling}
                            btnText='Подключить'
                            data-test='request-form'
                        />
                    </ST.Description>
                }
                {!isMobile &&
                    <ST.ImgWrap hasDomofon={hasDomofon}>
                        <picture>
                            <source srcSet={require('~/static/images/domofon/connect-domofon/connect.webp')} type='image/webp'/>
                            <ST.Img
                                src={require('~/static/images/domofon/connect-domofon/connect.png')}
                            />
                        </picture>
                        <ST.VideoWrap hasDomofon={hasDomofon}>
                            <ST.SVideo
                                hasDomofon={hasDomofon}
                                playsInline
                                muted
                                loop
                                autoPlay
                                src={`${IMG_PATH}/small.mp4`}
                            />
                        </ST.VideoWrap>
                    </ST.ImgWrap>
                }
            </ST.SWideContainer>

            {isMobile &&
                <ST.ImgWrap hasDomofon={hasDomofon}>
                    <picture>
                        <source srcSet={require('~/static/images/domofon/connect-domofon/connect-mobile.webp')} type='image/webp'/>
                        <ST.Img
                            src={require('~/static/images/domofon/connect-domofon/connect-mobile.png')}
                        />
                    </picture>
                    <ST.VideoWrap hasDomofon={hasDomofon}>
                        <ST.SVideo
                            hasDomofon={hasDomofon}
                            playsInline
                            muted
                            loop
                            autoPlay
                            src={`${IMG_PATH}/smallest.mp4`}
                        />
                    </ST.VideoWrap>
                </ST.ImgWrap>
            }

            {/* Анимированные картинки */}
            <ST.AnimImageWrap>
                {animatedImages.map((item) => {
                    const left = item.left ? `left: ${item.left} !important;` : '';
                    const right = item.right ? `right: ${item.right} !important;` : '';
                    return (
                        <picture key={item.id}>
                            <source srcSet={item.image_webp} type='image/webp'/>
                            <ST.AnimImage
                                src={item.image}
                                startPosition={isVisible ? '' : left + right}
                                params={item.params}
                                desktopParams={item.desktopParams}
                                laptopParams={item.laptopParams}
                                tabletParams={item.tabletParams}
                                mobileParams={item.mobileParams}
                            />
                        </picture>
                    );
                })}
            </ST.AnimImageWrap>
        </ST.ConnectDomofon>
    );
};
